/**
 * Account lifecycle utilities — V1 simplified contract.
 *
 * V1 customer-level lifecycle exposes only:
 *   • Active — default; customer is in normal operations.
 *   • Closed — relationship ended (relationship action, captures a reason).
 *
 * Backend keeps 'inactive' as a valid enum value for future flexibility
 * (multi-program subcollection unlock, etc.), but the V1 UI surfaces it as
 * "Inactive" only when programmatically present — operators cannot set it.
 *
 * Legacy 'paused' values are transparently normalized to 'inactive' on the
 * read path so older Firestore data renders correctly during the migration
 * deprecation window.
 *
 * Backwards-compatible: leads with no `accountStatus` field are treated as
 * "active" so existing behavior is unchanged.
 */

// Canonical V1 enum.
export const LIFECYCLE_STATUSES = ['active', 'inactive', 'closed'];
export const LIFECYCLE_DEFAULT = 'active';

/**
 * Normalize raw status from Firestore. Legacy 'paused' → 'inactive'.
 */
function normalizeStatus(status) {
  if (status === 'paused') return 'inactive';
  return LIFECYCLE_STATUSES.includes(status) ? status : LIFECYCLE_DEFAULT;
}

export function getAccountStatus(lead) {
  if (!lead || typeof lead !== 'object') return LIFECYCLE_DEFAULT;
  return normalizeStatus(lead.accountStatus);
}

export function isActiveAccount(lead) {
  return getAccountStatus(lead) === 'active';
}

/** True when the lead is in the V1 'inactive' state (or legacy 'paused'). */
export function isInactiveAccount(lead) {
  return getAccountStatus(lead) === 'inactive';
}

export function isClosedAccount(lead) {
  return getAccountStatus(lead) === 'closed';
}

export const LIFECYCLE_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  closed: 'Closed',
};

/** Tailwind pill classes for each status. Inactive uses slate to avoid the
 *  "attention needed" tone of the legacy amber Paused palette. */
export const LIFECYCLE_PILL_CLASSES = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-600 border-slate-300',
  closed: 'bg-gray-100 text-gray-600 border-gray-300',
};

/** Compact relative-time formatter (e.g. "just now", "3m ago", "2h ago",
 *  "3d ago", "Jan 12"). */
export function formatRelativeTime(input) {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 45) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 14) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Friendly actor label from a history entry. Gracefully handles missing
 *  display names by falling back to the email's local part, then a generic
 *  "a teammate" / "auto" label. */
export function formatActorLabel(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const uid = entry.actorUid || '';
  if (uid.startsWith('system:')) return 'auto';
  const email = entry.actorEmail;
  if (email && typeof email === 'string' && email.includes('@')) {
    const local = email.split('@')[0];
    if (local) return local;
  }
  return 'a teammate';
}

/** Most recent status-change entry for the lead (or null). */
export function getLastStatusChange(lead) {
  const history = Array.isArray(lead?.accountStatusHistory)
    ? lead.accountStatusHistory
    : null;
  if (!history || history.length === 0) return null;
  return history[history.length - 1];
}

/** Human-readable label for a Phase D outbound surface identifier. */
export const SURFACE_LABELS = {
  playbook_send_sms: 'Automated SMS (Playbook)',
  playbook_send_reminder: 'Balance Reminder',
  missed_call_reply: 'Missed-Call Text-Back',
  visit_sms: 'Visit / Service SMS',
};

export function formatSkipSurface(surface) {
  if (!surface) return 'Automation';
  return SURFACE_LABELS[surface] || surface;
}

/** Human-readable label for an audit entry kind. */
export const AUDIT_KIND_LABELS = {
  recurrence_skipped: 'Recurrence not seeded',
  autocharge_skipped: 'Auto-charge skipped',
  outbound_skipped: 'Automated message suppressed',
};

export function formatAuditKind(kind) {
  return AUDIT_KIND_LABELS[kind] || kind || 'Lifecycle event';
}
